/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { type CSSProperties } from "react";
import { theme } from "twin.macro";
import Label from "../Label";
import Paragraph from "../Paragraph";

import IconAdd from "@iconsV2/Edit/add.svg?react";
import IconRemove from "@iconsV2/Edit/remove.svg?react";
import IconTrash from "@iconsV2/Interrface/trash-02.svg?react";
import type { TSize } from "@/types/common";
import { MAX_IMPORTED_UNIT_QUANTITY } from "@/recourses/constants";

import {
  StyledInput,
  StyledWrapperBtnDecrement,
  StyledWrapperBtnIncrement,
  StyledWrapperBtnRemove,
  StyledWrapperCounterInput,
  StyledWrapperInput,
} from "./styles";

export interface CounterInputProps {
  label?: string;
  helpText?: string;
  error?: boolean;
  size?: TSize;
  showIconRemove?: boolean;
  measureType?: string;
  value: string;
  onIncrement: () => void;
  onDecrement: () => void;
  onRemove: () => void;
  onChange: (value: number | string) => void;
  maxUnits?: number;
  width?: CSSProperties["width"];
  justifyContent?: CSSProperties["justifyContent"];
  averageWeight?: number;
  isImported?: boolean;
  [key: string]: any;
}

const MAX_LENGTH = 6;
const MAX_LENGTH_STRING = 99_999;

const CounterInput = ({
  label,
  helpText,
  value,
  error = false,
  size,
  showIconRemove = true,
  maxUnits = 1000,
  width,
  justifyContent,
  measureType,
  averageWeight = 1,
  isImported = false,
  onIncrement = () => {
    throw new Error("onIncrement is not implemented");
  },
  onDecrement = () => {
    throw new Error("onDecrement is not implemented");
  },
  onRemove = () => {
    throw new Error("onRemove is not implemented");
  },
  onChange = () => {
    throw new Error("onChange is not implemented");
  },
  ...rest
}: CounterInputProps) => {
  // Max 6 units for imported items, otherwise use available stock.
  const effectiveMaxUnits = isImported ? Math.min(MAX_IMPORTED_UNIT_QUANTITY, maxUnits) : maxUnits;

  // Check if limit is due to import policy (not stock).
  const isLimitedByImportPolicy = isImported && maxUnits > MAX_IMPORTED_UNIT_QUANTITY;

  // Función que formatea el valor añadiendo una unidad de medida si está definida
  const formattedValue = (value: string): string => {
    return measureType && averageWeight >= 2 ? `${value} ${measureType}` : value;
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (averageWeight >= 2) {
      return;
    }

    const quantity = e.target.value;

    if (quantity === "") {
      onChange("");
      return;
    }

    if (quantity.length > MAX_LENGTH) {
      const numQuantity = Number(quantity.slice(0, MAX_LENGTH));
      if (!isNaN(numQuantity)) {
        onChange(numQuantity);
      }
      return;
    }

    const numQuantity = Number(quantity);
    if (!isNaN(numQuantity)) {
      // Block if imported and quantity exceeds limit
      // if (isImported && numQuantity > effectiveMaxUnits) {
      //   return onChange(effectiveMaxUnits);
      // }
      if (quantity.length <= MAX_LENGTH) {
        onChange(numQuantity);
      }
    }
  };
  const handleIncrement = () => {
    if (Number(value) + 1 > MAX_LENGTH_STRING) {
      return;
    }
    onIncrement();
  };

  const handleDecrement = () => {
    if (Number(value) - 1 < 0) {
      return;
    }
    onDecrement();
  };

  const hasError = error || Number(value) > effectiveMaxUnits;

  // Generate automatic help text ONLY when the limit is due to an import policy (not due to stock).
  const getHelpText = (): string => {
    // Si viene helpText desde afuera, tiene prioridad (mensajes de stock, etc.)
    if (helpText) return helpText;

    const currentValue = Number(value);

    // Show custom message ONLY if:
    // 1. Imported
    // 2. There are more than 6 in stock (the limit is due to import policy, not due to stock shortage)
    // 3. The user has reached or exceeded the limit of 6
    if (isLimitedByImportPolicy && currentValue > MAX_IMPORTED_UNIT_QUANTITY) {
      return `Max ${MAX_IMPORTED_UNIT_QUANTITY} unidades`;
    }

    return "";
  };

  const finalHelpText = getHelpText();

  return (
    <StyledWrapperCounterInput {...rest} $hasValue={!!value?.length}>
      {label && <Label sizeVariant="md">{label}</Label>}

      <StyledWrapperInput $width={width} $justifyContent={justifyContent}>
        <div>
          <StyledWrapperBtnDecrement onClick={handleDecrement} $size={size} disabled={value === "0"}>
            {value === "1" ? <IconTrash /> : <IconRemove />}
          </StyledWrapperBtnDecrement>

          <StyledInput
            type="text"
            value={formattedValue(value)}
            onChange={handleChangeInput}
            maxLength={MAX_LENGTH + (measureType ? 2 : 0)}
            $error={hasError}
            $size={size}
            autoComplete="off"
            disabled={averageWeight >= 2}
          />

          <StyledWrapperBtnIncrement onClick={handleIncrement} $size={size}>
            <IconAdd />
          </StyledWrapperBtnIncrement>
        </div>

        {showIconRemove && (
          <StyledWrapperBtnRemove onClick={onRemove}>
            <IconTrash />
          </StyledWrapperBtnRemove>
        )}
      </StyledWrapperInput>

      {finalHelpText && (
        <Paragraph sizeVariant="sm" color={hasError ? theme`colors.error.40` : theme`colors.neutral.10`}>
          {finalHelpText}
        </Paragraph>
      )}
    </StyledWrapperCounterInput>
  );
};

export default CounterInput;
